import React, { useState, useCallback } from "react";

const API_BASE = "http://localhost:5000";

const ADVERTISER_IDS = [
  "7273114331808661505",
  "7163132868963696641",
  "7163133059045408770",
  "7520170196233912338",
  "7587349812459257873",
  "7587348361360211984",
  "7555183871942033424",
  "7556299771632451592",
];

const today = () => new Date().toISOString().split("T")[0];
const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().split("T")[0];
const fmtIQD = (n) => n == null ? "—" : Math.round(n).toLocaleString("ar-IQ") + " د.ع";
const fmtNum = (n) => n == null ? "—" : Number(n).toLocaleString("ar-IQ");

const STATUS_COLORS = {
  count_unconfirmed: { bg: "#FFF3E0", text: "#E65100", label: "غير مؤكد" },
  count_no_answer:   { bg: "#F3E5F5", text: "#6A1B9A", label: "لا يرد" },
  count_waiting:     { bg: "#E3F2FD", text: "#1565C0", label: "قبل الطباعة" },
  count_prepared:    { bg: "#E8F5E9", text: "#2E7D32", label: "تم التجهيز" },
  count_cancelled:   { bg: "#FFEBEE", text: "#C62828", label: "ملغي" },
  count_success:     { bg: "#E0F2F1", text: "#00695C", label: "ناجح" },
  count_returned:    { bg: "#FBE9E7", text: "#BF360C", label: "راجع" },
};

// ─── Status Badge ──────────────────────────────────────────────────────────
function StatusBadge({ type, count }) {
  if (!count) return null;
  const s = STATUS_COLORS[type];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 20, fontSize: 11,
      background: s.bg, color: s.text, fontWeight: 600,
      fontFamily: "Cairo", whiteSpace: "nowrap",
    }}>
      {s.label} <span style={{ fontWeight: 800 }}>{count}</span>
    </span>
  );
}

// ─── Stat Pill ─────────────────────────────────────────────────────────────
function StatPill({ label, value, color = "#1a1a2e", small = false }) {
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #eee",
      borderRadius: 12,
      padding: small ? "10px 14px" : "14px 16px",
      display: "flex", flexDirection: "column", gap: 3,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    }}>
      <span style={{ fontSize: 11, color: "#999", fontFamily: "Cairo" }}>{label}</span>
      <span style={{ fontSize: small ? 15 : 18, fontWeight: 800, color, fontFamily: "Cairo" }}>{value}</span>
    </div>
  );
}

// ─── Campaign Card ─────────────────────────────────────────────────────────
function CampaignCard({ c }) {
  const [open, setOpen] = useState(false);
  const isProfit = c.total_profit > 0;

  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
      overflow: "hidden",
      border: "1px solid #f0f0f0",
      marginBottom: 12,
    }}>
      {/* Header */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          padding: "14px 16px",
          cursor: "pointer",
          borderBottom: open ? "1px solid #f5f5f5" : "none",
        }}
      >
        {/* Top Row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div style={{ flex: 1, marginLeft: 8 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: "#1a1a2e",
              fontFamily: "Cairo", lineHeight: 1.4, marginBottom: 3
            }}>
              {c.campaign_name}
            </div>
            {c.product_name && (
              <div style={{ fontSize: 11, color: "#999", fontFamily: "Cairo" }}>
                📦 {c.product_name}
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <span style={{
              padding: "3px 10px", borderRadius: 20, fontSize: 11, fontFamily: "Cairo",
              background: c.is_active ? "#E8F5E9" : "#F5F5F5",
              color: c.is_active ? "#2E7D32" : "#999",
            }}>
              {c.is_active ? "● نشطة" : "○ متوقفة"}
            </span>
            <span style={{ fontSize: 10, color: "#bbb", fontFamily: "Cairo" }}>
              {c.account_name} • {c.commission_rate}
            </span>
          </div>
        </div>

        {/* Stats Row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
          <StatPill label="الإنفاق" value={fmtIQD(c.total_cost_iqd)} color="#C62828" small />
          <StatPill label="الطلبات" value={fmtNum(c.count_financial)} color="#1565C0" small />
          <StatPill
            label="الربح"
            value={fmtIQD(c.total_profit)}
            color={isProfit ? "#2E7D32" : "#C62828"}
            small
          />
        </div>

        {/* Status Badges */}
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
          {Object.keys(STATUS_COLORS).map(k => (
            <StatusBadge key={k} type={k} count={c[k]} />
          ))}
        </div>

        {/* Cost & Profit per order */}
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{
            flex: 1, background: "#FFF8E1", borderRadius: 8, padding: "8px 10px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ fontSize: 11, color: "#F57F17", fontFamily: "Cairo" }}>تكلفة الطلب</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#F57F17", fontFamily: "Cairo" }}>
              {fmtIQD(c.cost_per_order)}
            </span>
          </div>
          <div style={{
            flex: 1, borderRadius: 8, padding: "8px 10px",
            background: isProfit ? "#E8F5E9" : "#FFEBEE",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ fontSize: 11, color: isProfit ? "#2E7D32" : "#C62828", fontFamily: "Cairo" }}>
              ربح الطلب
            </span>
            <span style={{ fontSize: 13, fontWeight: 800, color: isProfit ? "#2E7D32" : "#C62828", fontFamily: "Cairo" }}>
              {fmtIQD(c.profit_per_order)}
            </span>
          </div>
        </div>

        {/* Expand indicator */}
        <div style={{ textAlign: "center", marginTop: 8, color: "#ccc", fontSize: 12 }}>
          {open ? "▲ إخفاء التفاصيل" : "▼ الطلبات اليومية"}
        </div>
      </div>

      {/* Daily Orders */}
      {open && (
        <div style={{ padding: "12px 16px" }}>
          <div style={{ fontSize: 12, color: "#999", fontFamily: "Cairo", marginBottom: 10 }}>
            الطلبات اليومية
          </div>
          {c.daily_orders.length === 0 ? (
            <div style={{ textAlign: "center", color: "#ccc", fontFamily: "Cairo", fontSize: 13, padding: 16 }}>
              لا توجد طلبات
            </div>
          ) : (
            c.daily_orders.map((d, i) => (
              <div key={i} style={{
                padding: "10px 12px", borderRadius: 10, marginBottom: 6,
                background: "#FAFAFA", border: "1px solid #F0F0F0",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#333", fontFamily: "Cairo" }}>
                    {d.day}
                  </span>
                  <span style={{ fontSize: 12, color: "#1565C0", fontWeight: 700, fontFamily: "Cairo" }}>
                    {d.count_financial} طلب مالي
                  </span>
                </div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {Object.keys(STATUS_COLORS).map(k => (
                    <StatusBadge key={k} type={k} count={d[k]} />
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                  <span style={{ fontSize: 11, color: "#999", fontFamily: "Cairo" }}>
                    صافي اليوم
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#333", fontFamily: "Cairo" }}>
                    {fmtIQD(d.total_net)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Summary Bar ───────────────────────────────────────────────────────────
function SummaryBar({ campaigns }) {
  const total_cost    = campaigns.reduce((s, c) => s + (c.total_cost_iqd || 0), 0);
  const total_orders  = campaigns.reduce((s, c) => s + (c.count_financial || 0), 0);
  const total_profit  = campaigns.reduce((s, c) => s + (c.total_profit || 0), 0);
  const total_success = campaigns.reduce((s, c) => s + (c.count_success || 0), 0);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
      <StatPill label="إجمالي الإنفاق" value={fmtIQD(total_cost)} color="#C62828" />
      <StatPill label="إجمالي الطلبات" value={fmtNum(total_orders)} color="#1565C0" />
      <StatPill label="إجمالي الربح" value={fmtIQD(total_profit)} color={total_profit > 0 ? "#2E7D32" : "#C62828"} />
      <StatPill label="الطلبات الناجحة" value={fmtNum(total_success)} color="#00695C" />
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────
export default function TikTokDashboard() {
  const [startDate, setStartDate]     = useState(daysAgo(7));
  const [endDate, setEndDate]         = useState(today());
  const [exchangeRate, setExchangeRate] = useState("1480");
  const [campaigns, setCampaigns]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [fetched, setFetched]         = useState(false);
  const [sortBy, setSortBy]           = useState("total_orders");
  const [filterActive, setFilterActive] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(""); setCampaigns([]); setFetched(false);
    try {
      const res = await fetch(`${API_BASE}/tiktok/campaigns-orders/multi-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          advertiser_ids: ADVERTISER_IDS,
          start_date: startDate,
          end_date: endDate,
          exchange_rate: parseFloat(exchangeRate),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "فشل الطلب");
      const list = Array.isArray(data) ? data : data.campaigns || [];
      setCampaigns(list);
      setFetched(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, exchangeRate]);

  const displayed = campaigns
    .filter(c => filterActive ? c.is_active : true)
    .filter(c => c.count_financial > 0)
    .sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));

  return (
    <div style={{
      minHeight: "100vh",
      background: "#F7F8FC",
      fontFamily: "Cairo, sans-serif",
      direction: "rtl",
      maxWidth: 480,
      margin: "0 auto",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── Header ── */}
      <div style={{
        background: "#fff",
        padding: "16px 16px 0",
        boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
        position: "sticky", top: 0, zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: "linear-gradient(135deg, #fe2c55, #ff6b35)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
          }}>🎵</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#1a1a2e", fontFamily: "Cairo" }}>
              TikTok Ads
            </div>
            <div style={{ fontSize: 11, color: "#999", fontFamily: "Cairo" }}>تقرير الحملات الإعلانية</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, color: "#aaa", marginBottom: 3, fontFamily: "Cairo" }}>من</div>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #eee",
                fontSize: 12, fontFamily: "Cairo", background: "#FAFAFA", boxSizing: "border-box",
              }} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#aaa", marginBottom: 3, fontFamily: "Cairo" }}>إلى</div>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #eee",
                fontSize: 12, fontFamily: "Cairo", background: "#FAFAFA", boxSizing: "border-box",
              }} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: "#aaa", marginBottom: 3, fontFamily: "Cairo" }}>سعر الدولار (د.ع)</div>
            <input
              type="number"
              value={exchangeRate}
              onChange={e => setExchangeRate(e.target.value)}
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #eee",
                fontSize: 13, fontFamily: "Cairo", background: "#FAFAFA", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <button onClick={fetchData} disabled={loading} style={{
              padding: "9px 20px", borderRadius: 8, border: "none", cursor: "pointer",
              background: loading ? "#eee" : "linear-gradient(135deg, #fe2c55, #ff6b35)",
              color: loading ? "#aaa" : "#fff", fontSize: 13, fontWeight: 700,
              fontFamily: "Cairo", whiteSpace: "nowrap",
            }}>
              {loading ? "⏳" : "🔍 جلب"}
            </button>
          </div>
        </div>

        {/* Sort & Filter */}
        {fetched && (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 10, scrollbarWidth: "none" }}>
            {[
              { key: "total_orders", label: "الطلبات" },
              { key: "total_profit", label: "الربح" },
              { key: "total_cost_iqd", label: "الإنفاق" },
              { key: "count_success", label: "الناجح" },
            ].map(opt => (
              <button key={opt.key} onClick={() => setSortBy(opt.key)} style={{
                padding: "5px 12px", borderRadius: 20, border: "none", cursor: "pointer",
                background: sortBy === opt.key ? "#fe2c55" : "#F0F0F0",
                color: sortBy === opt.key ? "#fff" : "#666",
                fontSize: 12, fontFamily: "Cairo", whiteSpace: "nowrap", flexShrink: 0,
              }}>{opt.label}</button>
            ))}
            <button onClick={() => setFilterActive(!filterActive)} style={{
              padding: "5px 12px", borderRadius: 20, border: "none", cursor: "pointer",
              background: filterActive ? "#1565C0" : "#F0F0F0",
              color: filterActive ? "#fff" : "#666",
              fontSize: 12, fontFamily: "Cairo", whiteSpace: "nowrap", flexShrink: 0,
            }}>نشطة فقط</button>
          </div>
        )}
      </div>

      {/* ── Content ── */}
      <div style={{ padding: "16px 12px" }}>

        {/* Error */}
        {error && (
          <div style={{
            background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 10,
            padding: "12px 14px", marginBottom: 14, color: "#C62828",
            fontSize: 13, fontFamily: "Cairo",
          }}>⚠️ {error}</div>
        )}

        {/* Summary */}
        {fetched && displayed.length > 0 && <SummaryBar campaigns={displayed} />}

        {/* Count */}
        {fetched && (
          <div style={{ fontSize: 12, color: "#aaa", fontFamily: "Cairo", marginBottom: 10 }}>
            {displayed.length} حملة
          </div>
        )}

        {/* Campaign Cards */}
        {displayed.map(c => <CampaignCard key={c.campaign_id} c={c} />)}

        {/* Empty */}
        {fetched && displayed.length === 0 && !loading && (
          <div style={{ textAlign: "center", padding: 60, color: "#ccc", fontFamily: "Cairo" }}>
            لا توجد حملات بطلبات في هذه الفترة
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: "center", padding: 60, color: "#fe2c55", fontFamily: "Cairo", fontSize: 16 }}>
            ⏳ جاري جلب البيانات...
          </div>
        )}
      </div>
    </div>
  );
}